import React, { memo, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { FormHelperText } from '@mui/material'

import { FieldSettings } from '../../../ana-toolbox'
import { combinedFields } from '../../utils/yup/main'
import { Translate } from '../translate/translate'

const fieldsByName = Object.fromEntries(Object.values(combinedFields).map((field) => [field.name, field]))

export type FieldHelperTextProps = {
  name: string
  className?: string
  error?: string
  helperData?: object
  helperText?: React.ReactNode
}

export const FieldHelperText = memo(({ name, helperData, error, helperText, className }: FieldHelperTextProps) => {
  const { t } = useTranslation()

  const settings: FieldSettings | undefined = fieldsByName[name]

  const content = useMemo(() => {
    if (error) {
      const fieldName = t(typeof settings?.label === 'string' ? settings.label || name : name)

      const isEdgeCase = Array.isArray(fieldName) && !!fieldName.find((field) => typeof field !== 'string')

      const fallbackError = t('generic_form_error', {
        // In cases where the translation contains JSX elements and we try to render it,
        // there will be an error like: 'Some text ,[object Object],' to avoid this, we use
        // another mechanism for receiving the translations: t(`${name}_form_error`)
        fieldName: isEdgeCase ? t(`${name.toLocaleLowerCase()}_form_error`) : fieldName,
        postProcess: [],
      })

      return (
        <Translate fallback={fallbackError} fieldName={fieldName}>
          {error}
        </Translate>
      )
    }

    if (helperText)
      return (
        <Translate {...helperData} options={{ postProcess: 'markdown' }}>
          {helperText}
        </Translate>
      )

    if (settings?.justificativeTooltipText) {
      return <Translate>{settings?.justificativeTooltipText}</Translate>
    }

    return null
  }, [name, helperText, helperData, settings?.justificativeTooltipText, settings?.label, t, error])

  return (
    <FormHelperText className={className} error={Boolean(error)} hidden={!content}>
      {content}
    </FormHelperText>
  )
})

FieldHelperText.displayName = 'FieldHelperText'
